import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import styles from '@/pages/ProjectsPage.module.css';

type AiRecommendation = {
  id: string;
  kind: 'fact' | 'hypothesis';
  status: 'pending' | 'accepted' | 'dismissed';
  title: string;
  payload: { body?: string };
  evidence: { id: string; label: string; value?: unknown }[];
};

function KindBadge({ kind }: { kind: AiRecommendation['kind'] }) {
  const isFact = kind === 'fact';
  return (
    <span
      style={{
        fontSize: '0.75rem',
        padding: '2px 8px',
        borderRadius: 999,
        marginLeft: 8,
        background: isFact ? '#ccfbf1' : '#fef3c7',
        color: isFact ? '#0f766e' : '#92400e',
      }}
    >
      {isFact ? 'Feit' : 'Hypothese'}
    </span>
  );
}

export function AudienceAiPanel({ audienceId }: { audienceId: string }) {
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ['audience-ai', audienceId],
    queryFn: () =>
      apiFetch<{ recommendations: AiRecommendation[] }>(
        `/api/audiences/${audienceId}/ai-recommendations`,
      ),
  });

  const generate = useMutation({
    mutationFn: () =>
      apiFetch<{ recommendations: AiRecommendation[] }>(
        `/api/audiences/${audienceId}/ai-recommendations/generate`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (data) => {
      qc.setQueryData(['audience-ai', audienceId], {
        recommendations: data.recommendations,
      });
    },
  });

  const updateStatus = useMutation({
    mutationFn: (args: { recId: string; status: 'accepted' | 'dismissed' }) =>
      apiFetch(`/api/audiences/${audienceId}/ai-recommendations/${args.recId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: args.status }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['audience-ai', audienceId] }),
  });

  const pending = (listQ.data?.recommendations ?? []).filter((r) => r.status === 'pending');

  return (
    <section>
      <div className={styles.row} style={{ marginBottom: 'var(--space-md)' }}>
        <p className={styles.mutedSmall} style={{ flex: 1 }}>
          AI-advies op basis van meetbare evidence (scores, insights, scraper). Hypotheses zijn
          expliciet gelabeld — geen LLM-scores.
        </p>
        <Button type="button" onClick={() => generate.mutate()} disabled={generate.isPending}>
          Analyse genereren
        </Button>
      </div>

      {listQ.isLoading ? <LoadingState /> : null}
      {generate.isError ? (
        <StateMessage title="Genereren mislukt">
          {(generate.error as Error).message}
        </StateMessage>
      ) : null}

      {!listQ.isLoading && !(listQ.data?.recommendations.length ?? 0) ? (
        <StateMessage title="Nog geen advies">
          Genereer een analyse. Zonder LLM verschijnen alleen deterministische feiten; met gateway
          ook interpretaties.
        </StateMessage>
      ) : null}

      <ul className={styles.list}>
        {(listQ.data?.recommendations ?? []).map((rec) => (
          <li key={rec.id} className={styles.card} style={{ marginBottom: 'var(--space-md)' }}>
            <h3 style={{ marginTop: 0 }}>
              {rec.title}
              <KindBadge kind={rec.kind} />
              {rec.status !== 'pending' ? (
                <span className={styles.mutedSmall}> · {rec.status}</span>
              ) : null}
            </h3>
            <p>{rec.payload?.body ?? '—'}</p>
            {(rec.evidence?.length ?? 0) > 0 ? (
              <>
                <p className={styles.mutedSmall}>Evidence:</p>
                <ul className={styles.list}>
                  {rec.evidence.map((ev) => (
                    <li key={ev.id} className={styles.mutedSmall}>
                      {ev.label} ({ev.id})
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {rec.status === 'pending' ? (
              <div className={styles.row}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => updateStatus.mutate({ recId: rec.id, status: 'accepted' })}
                  disabled={updateStatus.isPending}
                >
                  Accepteren
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => updateStatus.mutate({ recId: rec.id, status: 'dismissed' })}
                  disabled={updateStatus.isPending}
                >
                  Negeren
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {pending.length > 0 ? (
        <p className={styles.mutedSmall}>{pending.length} openstaand advies/adviezen.</p>
      ) : null}
    </section>
  );
}
